import { Link } from "react-router-dom";
import { ChevronRight, MapPin, User, Gauge } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDateTime, durationLabel } from "@/lib/time";
import { DECISION_LABEL } from "@/lib/take5";
import { RESULT_KEY } from "@/lib/labels";
import { SyncIcon } from "@/components/SyncIcon";

export const Take5Card = ({ t, showUser }) => (
  <Link to={`/take5/${t.id}`} data-testid={`take5-card-${t.id}`} className="group block rounded-xl border border-border bg-card p-4 transition-colors hover:border-amber-500/50">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-base font-bold" data-testid="take5-card-task">{t.task || "Tâche non décrite"}</p>
        <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {t.site?.name}{t.location ? ` · ${t.location}` : ""}</p>
        {showUser && <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><User className="h-3.5 w-3.5" /> {t.user_name}</p>}
        {t.as_participant && <p data-testid="take5-card-participant" className="mt-0.5 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"><User className="h-3.5 w-3.5" /> Participant · Take 5 collectif de {t.responsible_name || t.user_name}</p>}
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </div>
    <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 font-data text-xs text-slate-300 sm:grid-cols-3">
      <span data-testid="take5-card-start">Début : {fmtDateTime(t.start_at, t.site)}</span>
      <span data-testid="take5-card-duration">Durée : {durationLabel(t.duration_minutes)}</span>
      <span data-testid="take5-card-end">Fin prévue : {fmtDateTime(t.planned_end_at, t.site)}</span>
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <StatusBadge status={t.status} testId="take5-card-status" />
      <SyncIcon id={t.id} testId={`take5-card-sync-${t.id}`} />
      {t.status === "archivee" && t.decision && <span data-testid="take5-card-decision" className="text-xs text-muted-foreground">Décision d'origine : {DECISION_LABEL[t.decision]}</span>}
      {t.status === "archivee" && !t.decision && <span className="text-xs text-muted-foreground">Aucune décision prise</span>}
      {t.superseded_by && <StatusBadge status="superseded" />}
      {t.revision > 1 && <span className="font-data text-xs text-orange-300">Révision {t.revision}</span>}
    </div>
  </Link>
);

export const InspectionCard = ({ i, showUser }) => (
  <Link to={`/inspections/${i.id}`} data-testid={`inspection-card-${i.id}`} className="group block rounded-xl border border-border bg-card p-4 transition-colors hover:border-amber-500/50">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-base font-bold"><span className="font-data text-amber-300">{i.equipment?.code}</span> · {i.equipment?.name}</p>
        <p className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {i.site?.name}</p>
        {showUser && <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><User className="h-3.5 w-3.5" /> {i.user_name}{i.performed_by_workplace_name ? ` · ${i.performed_by_workplace_name}` : ""}{i.performed_by_team_name ? ` · Équipe ${i.performed_by_team_name}` : ""}</p>}
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <StatusBadge status={RESULT_KEY(i)} />
      <SyncIcon id={i.id} testId={`inspection-card-sync-${i.id}`} />
      <span className="font-data text-xs text-slate-300"><Gauge className="mr-1 inline h-3.5 w-3.5" />{i.finalized_at ? `Finalisée : ${fmtDateTime(i.finalized_at, i.site)}` : `Commencée : ${fmtDateTime(i.started_at, i.site)}`}</span>
      {i.anomaly_count > 0 && <span className="text-xs text-amber-300">{i.anomaly_count} anomalie(s)</span>}
    </div>
  </Link>
);
